export interface PassportSample {
  photo: string;
  photoAlt: string;
  note: string;
  date: string;
  tag: string;
  place: string;
}

export interface PassportStage {
  no: string;
  icon: string;
  title: string;
  desc: string;
  points: string[];
  recordCount: number;
  sample?: PassportSample;
}

export interface PassportProfile {
  code: string;
  product: string;
  origin: string;
  verifyTime: string;
  sample: PassportSample;
}

export const passportMeta = {
  brand: '云市民 ePin 食安码',
  statusBadge: '验证状态',
  statusTitle: '已核验，安心选购',
  statusDesc: '来源、检测与流转记录完整有效',
  verifyPrefix: '本码已通过平台核验',
  journeyCta: '查看完整旅程',
  sectionTitle: '从一粒种子，到你的餐桌',
  sectionSubtitle: '本批次走过的八个阶段 · 点开阶段卡查看真实留痕',
  sampleLabel: '留痕示例',
};

export const passportBaseStages: PassportStage[] = [
  {
    no: '01',
    icon: 'ri-seedling-line',
    title: '播种／投苗建档',
    desc: '建立种植／养殖档案，锁定品种、产区与生产主体。',
    points: ['记录品种与播种／投苗日期', '登记基地与种养方式', '一物一码前的生产源头'],
    recordCount: 1,
  },
  {
    no: '02',
    icon: 'ri-plant-line',
    title: '种植／养殖管理',
    desc: '田间与养殖过程留痕，投入品与农事操作全程可查。',
    points: ['灌溉、施肥、防疫摘要', '环境与生长关键记录', '形成生产记录摘要'],
    recordCount: 1,
  },
  {
    no: '03',
    icon: 'ri-leaf-line',
    title: '采摘／出栏',
    desc: '采收或出栏即时登记，当日完成分级处理。',
    points: ['采收／出栏日期时间', '采收人员与生产主体', '鲜度与分级处理记录'],
    recordCount: 1,
  },
  {
    no: '04',
    icon: 'ri-flask-line',
    title: '检测与审核',
    desc: '送检合格并经平台审核，形成可信的检测结论。',
    points: ['检测机构与报告编号', '检测结论与关键项目', '审核通过后方可赋码'],
    recordCount: 1,
  },
  {
    no: '05',
    icon: 'ri-qr-scan-2-line',
    title: '包装赋码',
    desc: '包装定规格、定保质期，签发唯一 ePin 食安码。',
    points: ['包装日期与规格', '保质期与储存条件', '一批一码／一品一码'],
    recordCount: 1,
  },
  {
    no: '06',
    icon: 'ri-truck-line',
    title: '入仓与运输',
    desc: '扫码入仓出库，冷链与温控流转节点逐段留痕。',
    points: ['入仓时间与仓库', '产地仓 → 城市仓／门店', '冷链或温控摘要'],
    recordCount: 1,
  },
  {
    no: '07',
    icon: 'ri-store-3-line',
    title: '上架、购买与履约',
    desc: '门店与线上云店上架，下单绑定批次并完成履约。',
    points: ['上架对应批次与库存', '下单绑定 ePin 与批次', '消费者扫码验真'],
    recordCount: 1,
  },
  {
    no: '08',
    icon: 'ri-customer-service-2-line',
    title: '售后、投诉与召回',
    desc: '支持评价、投诉与退货，异常商品一键冻结并触发召回。',
    points: ['评价、投诉与退货记录', '异常商品冻结与召回', '全过程留痕可审计'],
    recordCount: 0,
  },
];

export const passportProfiles: PassportProfile[] = [
  {
    code: 'epn_jgan_b_7f3a91c2d4',
    product: '尤溪金柑 · 精品礼盒',
    origin: '福建尤溪 · 中仙产区',
    verifyTime: '2026-09-23 08:00',
    sample: {
      photo:
        'https://readdy.ai/api/search-image?query=Sunny%20hillside%20citrus%20orchard%20with%20young%20golden%20kumquat%20trees%20in%20neat%20rows%2C%20a%20farmer%20inspecting%20a%20seedling%20with%20a%20notebook%2C%20soft%20morning%20light%2C%20fresh%20green%20leaves%20and%20warm%20golden%20fruit%2C%20clean%20natural%20documentary%20photography%2C%20high%20detail&width=720&height=440&seq=passport-sample-orchard-01&orientation=landscape',
      photoAlt: '播种／投苗建档留痕照片',
      note: '尤溪金柑完成播种并建立生产档案。',
      date: '2026-05-12',
      tag: '播种／投苗建档',
      place: '尤溪县中仙乡高山基地',
    },
  },
  {
    code: 'EPIN-CQ-20250918-000873',
    product: '清远走地鸡（鲜活）',
    origin: '广东清远 · 源潭产区',
    verifyTime: '2026-09-22 17:40',
    sample: {
      photo:
        'https://readdy.ai/api/search-image?query=Free%20range%20chicken%20farm%20with%20healthy%20chickens%20roaming%20on%20green%20pasture%20under%20soft%20daylight%2C%20clean%20modern%20rural%20poultry%20breeding%20base%2C%20natural%20documentary%20photography%2C%20high%20detail&width=720&height=440&seq=passport-sample-poultry-01&orientation=landscape',
      photoAlt: '投苗建档留痕照片',
      note: '清远走地鸡完成投苗并建立养殖档案。',
      date: '2026-05-20',
      tag: '播种／投苗建档',
      place: '清城区源潭镇生态养殖基地',
    },
  },
];

export const passportDefault = passportProfiles[0];

export const passportActions = [
  { key: 'service', label: '售后咨询', icon: 'ri-customer-service-2-line', href: '/contact/business' },
  { key: 'complaint', label: '质量投诉', icon: 'ri-error-warning-line', href: '/contact/business' },
  { key: 'report', label: '举报疑似仿冒', icon: 'ri-flag-line', href: '/contact/business' },
];

export const passportHotline = {
  label: '400-800-1688',
  tel: '4008001688',
};